/**
 * Framework Detection Utilities
 * Provides comprehensive detection for frontend and backend frameworks
 */

import { FRAMEWORK_CONFIG } from './frameworkConfig';

/**
 * Detect if source code is a Helm chart
 * A Helm chart has Chart.yaml at root (with apiVersion: v2 or v1)
 * and optionally a templates/ directory.
 */
export const detectHelmChart = (sourceCode) => {
  const files = Object.keys(sourceCode);
  // Direct Chart.yaml at root
  if (sourceCode['Chart.yaml'] || sourceCode['Chart.yml']) {
    return true;
  }
  // Chart.yaml in any subdirectory (e.g. helm/Chart.yaml)
  if (files.some(f => f.endsWith('/Chart.yaml') || f.endsWith('/Chart.yml'))) {
    return true;
  }
  // Fallback: templates/ dir alongside a values.yaml (anywhere in the tree)
  const hasTemplates = files.some(f => f.startsWith('templates/') || f.includes('/templates/'));
  const hasValues = files.some(f => f === 'values.yaml' || f === 'values.yml' || f.endsWith('/values.yaml') || f.endsWith('/values.yml'));
  if (hasTemplates && hasValues) {
    return true;
  }
  return false;
};

/**
 * Detect if source code is a Docusaurus project
 */
export const detectDocusaurus = (sourceCode) => {
  const packageJsonContent = sourceCode['package.json'];
  if (packageJsonContent) {
    try {
      const packageJson = typeof packageJsonContent === 'string' 
        ? JSON.parse(packageJsonContent) 
        : packageJsonContent;
      const dependencies = {...(packageJson.dependencies || {}), ...(packageJson.devDependencies || {})};
      return !!(dependencies['@docusaurus/core'] || dependencies['@docusaurus/preset-classic']);
    } catch (e) {
      return false;
    }
  }
  return !!sourceCode['docusaurus.config.js'] || !!sourceCode['docusaurus.config.ts'];
};

/**
 * Detect if source code is a React Native project
 */
export const detectReactNative = (sourceCode) => {
  const packageJsonContent = sourceCode['package.json'];
  if (packageJsonContent) {
    try {
      const packageJson = typeof packageJsonContent === 'string' 
        ? JSON.parse(packageJsonContent) 
        : packageJsonContent;
      const dependencies = {...(packageJson.dependencies || {}), ...(packageJson.devDependencies || {})};
      return !!(dependencies['react-native'] || dependencies['expo']);
    } catch (e) {
      return false;
    }
  }
  return !!sourceCode['app.json'] && !!sourceCode['App.js'];
};

/**
 * Detect if source code is a Vite-based React project
 * @param {Object} sourceCode - Source code files object
 * @returns {boolean} - true if Vite + React is detected
 */
export const detectViteReact = (sourceCode) => {
  const packageJsonContent = sourceCode['package.json'];
  if (packageJsonContent) {
    try {
      // Handle both string and already-parsed object
      const packageJson = typeof packageJsonContent === 'string' 
        ? JSON.parse(packageJsonContent) 
        : packageJsonContent;
      const dependencies = {...(packageJson.dependencies || {}), ...(packageJson.devDependencies || {})};
      const scripts = packageJson.scripts || {};
      
      // Check for Vite + React combination
      const hasVite = !!(dependencies['vite'] || scripts.start?.includes('vite') || scripts.dev?.includes('vite'));
      const hasReact = !!(dependencies['react'] || dependencies['react-dom']);
      
      console.log(`🔍 detectViteReact check:`, { hasVite, hasReact, scripts: scripts.start });
      
      return hasVite && hasReact;
    } catch (e) {
      console.error('⚠️ detectViteReact error:', e);
      return false;
    }
  }
  
  // Check for vite.config file
  const hasViteConfig = !!(sourceCode['vite.config.js'] || sourceCode['vite.config.ts'] || sourceCode['vite.config.mjs']);
  if (hasViteConfig) {
    console.log(`🔍 detectViteReact found vite config file`);
  }
  return hasViteConfig;
};

/**
 * Detect if source code is a Next.js project
 */
export const detectNextJs = (sourceCode) => {
  const packageJsonContent = sourceCode['package.json'];
  if (packageJsonContent) {
    try {
      const packageJson = typeof packageJsonContent === 'string' 
        ? JSON.parse(packageJsonContent) 
        : packageJsonContent;
      const dependencies = {...(packageJson.dependencies || {}), ...(packageJson.devDependencies || {})};
      return !!dependencies['next'];
    } catch (e) {
      return false;
    }
  }
  return !!sourceCode['next.config.js'] || !!sourceCode['next.config.mjs'] || !!sourceCode['next.config.ts'];
};

/**
 * Detect if source code is an Angular project
 */
export const detectAngular = (sourceCode) => {
  const packageJsonContent = sourceCode['package.json'];
  if (packageJsonContent) {
    try {
      const packageJson = typeof packageJsonContent === 'string' 
        ? JSON.parse(packageJsonContent) 
        : packageJsonContent;
      const dependencies = {...(packageJson.dependencies || {}), ...(packageJson.devDependencies || {})};
      return !!(dependencies['@angular/core'] || dependencies['@angular/cli']);
    } catch (e) {
      return false;
    }
  }
  return !!sourceCode['angular.json'];
};

/**
 * Detect if source code is a Vue.js project
 */
export const detectVue = (sourceCode) => {
  const packageJsonContent = sourceCode['package.json'];
  if (packageJsonContent) {
    try {
      const packageJson = typeof packageJsonContent === 'string' 
        ? JSON.parse(packageJsonContent) 
        : packageJsonContent;
      const dependencies = {...(packageJson.dependencies || {}), ...(packageJson.devDependencies || {})};
      return !!(dependencies['vue'] || dependencies['@vue/cli-service']);
    } catch (e) {
      return false;
    }
  }
  return !!sourceCode['vue.config.js'];
};

/**
 * Detect if source code is a jQuery project
 */
export const detectJQuery = (sourceCode) => {
  const packageJsonContent = sourceCode['package.json'];
  if (packageJsonContent) {
    try {
      const packageJson = typeof packageJsonContent === 'string' 
        ? JSON.parse(packageJsonContent) 
        : packageJsonContent;
      const dependencies = {...(packageJson.dependencies || {}), ...(packageJson.devDependencies || {})};
      if (dependencies['jquery']) return true;
    } catch (e) {
      // Continue to file-based detection
    }
  }
  
  // Check for jQuery in HTML files
  const htmlFiles = Object.keys(sourceCode).filter(f => f.endsWith('.html'));
  for (const file of htmlFiles) {
    const content = sourceCode[file] || '';
    if (content.includes('jquery') || content.includes('jQuery')) {
      return true;
    }
  }
  
  return false;
};

/**
 * Detect if source code is an ASP.NET frontend project (Blazor/Razor Pages)
 */
export const detectASPNetFrontend = (sourceCode) => {
  const hasCsproj = Object.keys(sourceCode).some(f => f.endsWith('.csproj'));
  const hasRazor = Object.keys(sourceCode).some(f => f.endsWith('.cshtml') || f.endsWith('.razor'));
  
  // ASP.NET frontend has both .csproj and Razor/Blazor files
  return hasCsproj && hasRazor;
};

/**
 * Detect backend framework from source code files
 * @param {Object} sourceCode - Source code files object
 * @returns {string} - Detected backend framework
 */
export const detectBackendFramework = (sourceCode) => {
  try {
    // Check for Helm chart first (infrastructure, not a traditional backend)
    if (detectHelmChart(sourceCode)) {
      return 'helm';
    }

    // Check package.json for Node.js frameworks
    const packageJsonContent = sourceCode['package.json'];
    if (packageJsonContent) {
      const packageJson = typeof packageJsonContent === 'string' 
        ? JSON.parse(packageJsonContent) 
        : packageJsonContent;
      const dependencies = {...(packageJson.dependencies || {}), ...(packageJson.devDependencies || {})};
      
      // Node.js is default, no special detection needed
      if (dependencies.express || dependencies.fastify || dependencies['@mia-platform/custom-plugin-lib']) {
        return 'node';
      }
    }
    
    // Check for Go files
    if (sourceCode['go.mod'] || sourceCode['main.go']) {
      return 'go';
    }
    
    // Check for Python files
    if (sourceCode['requirements.txt'] || sourceCode['main.py'] || sourceCode['app.py']) {
      return 'python';
    }
    
    // Check for Java files
    if (sourceCode['pom.xml'] || Object.keys(sourceCode).some(f => f.endsWith('.java'))) {
      return 'java';
    }
    
    // Check for .NET files (backend API, not frontend)
    const hasCsproj = Object.keys(sourceCode).some(f => f.endsWith('.csproj'));
    const hasCsFiles = Object.keys(sourceCode).some(f => f.endsWith('.cs'));
    const hasRazor = Object.keys(sourceCode).some(f => f.endsWith('.cshtml') || f.endsWith('.razor'));
    if (hasCsproj && hasCsFiles && !hasRazor) {
      return 'dotnet';
    }
    
    // Check for Rust files
    if (sourceCode['Cargo.toml'] || Object.keys(sourceCode).some(f => f.endsWith('.rs'))) {
      return 'rust';
    }
    
    // Check for PHP files
    const hasPhpFiles = Object.keys(sourceCode).some(f => f.endsWith('.php'));
    if (hasPhpFiles || sourceCode['composer.json']) {
      return 'php';
    }
    
    // Check for Kotlin files
    const hasKotlinFiles = Object.keys(sourceCode).some(f => f.endsWith('.kt'));
    const hasGradleKts = sourceCode['build.gradle.kts'] || sourceCode['build.gradle'];
    if (hasKotlinFiles || hasGradleKts) {
      return 'kotlin';
    }
    
    // Check for C++ files
    const hasCppFiles = Object.keys(sourceCode).some(f => f.endsWith('.cpp') || f.endsWith('.cc') || f.endsWith('.cxx'));
    const hasCMake = sourceCode['CMakeLists.txt'];
    const hasMakefile = sourceCode['Makefile'];
    if (hasCppFiles || hasCMake || hasMakefile) {
      return 'cpp';
    }
    
    // Check for Q# files
    const hasQSharpFiles = Object.keys(sourceCode).some(f => f.endsWith('.qs'));
    if (hasQSharpFiles) {
      return 'qsharp';
    }
    
    // Check for Elixir files
    const hasElixirFiles = Object.keys(sourceCode).some(f => f.endsWith('.ex') || f.endsWith('.exs'));
    const hasMixExs = sourceCode['mix.exs'];
    if (hasElixirFiles || hasMixExs) {
      return 'elixir';
    }
    
    // Check for CRUD service indicators (collections JSON files)
    const hasCollectionFiles = Object.keys(sourceCode).some(f => f.includes('collection') && f.endsWith('.json'));
    const hasCrudConfig = sourceCode['crud.config.json'] || sourceCode['collections.json'];
    if (hasCollectionFiles || hasCrudConfig) {
      return 'crud';
    }
    
    // Default to Node.js
    return 'node';
  } catch (error) {
    console.warn('⚠️ Error detecting backend framework:', error);
    return 'node';
  }
};

/**
 * Detect service type from source code files (more reliable than service metadata)
 * @param {Object} sourceCode - Source code files object
 * @returns {Object} - {isFrontend: boolean, isBackend: boolean, isCrud: boolean, detectedFramework: string}
 */
export const detectServiceTypeFromFiles = (sourceCode) => {
  if (!sourceCode || Object.keys(sourceCode).length === 0) {
    return {isFrontend: false, isBackend: false, isCrud: false, detectedFramework: null};
  }
  
  const files = Object.keys(sourceCode);
  
  // PRIORITY 0: Helm chart detection (infrastructure, not a software framework)
  if (detectHelmChart(sourceCode)) {
    return {isFrontend: false, isBackend: true, isCrud: false, detectedFramework: 'helm'};
  }

  // PRIORITY 1: Check for frontend frameworks FIRST (they are more specific than backend language detection)
  // A React app might have server.py for serving, but it's still a frontend app
  if (detectViteReact(sourceCode)) {
    return {isFrontend: true, isBackend: false, isCrud: false, detectedFramework: 'react'};
  }
  if (detectNextJs(sourceCode)) {
    return {isFrontend: true, isBackend: false, isCrud: false, detectedFramework: 'nextjs'};
  }
  if (detectAngular(sourceCode)) {
    return {isFrontend: true, isBackend: false, isCrud: false, detectedFramework: 'angular'};
  }
  if (detectVue(sourceCode)) {
    return {isFrontend: true, isBackend: false, isCrud: false, detectedFramework: 'vue'};
  }
  if (detectReactNative(sourceCode)) {
    return {isFrontend: true, isBackend: false, isCrud: false, detectedFramework: 'reactnative'};
  }
  if (detectDocusaurus(sourceCode)) {
    return {isFrontend: true, isBackend: false, isCrud: false, detectedFramework: 'docusaurus'};
  }
  if (detectASPNetFrontend(sourceCode)) {
    return {isFrontend: true, isBackend: false, isCrud: false, detectedFramework: 'aspnet-frontend'};
  }
  
  // PRIORITY 2: Backend indicators (only check if NOT a frontend framework)
  const backendIndicators = {
    helm: () => detectHelmChart(sourceCode),
    java: () => files.some(f => f.endsWith('.java')) || sourceCode['pom.xml'] || sourceCode['build.gradle'],
    go: () => sourceCode['go.mod'] || sourceCode['main.go'],
    python: () => sourceCode['requirements.txt'] || sourceCode['main.py'] || sourceCode['app.py'] || files.some(f => f.endsWith('.py')),
    rust: () => sourceCode['Cargo.toml'] || files.some(f => f.endsWith('.rs')),
    php: () => files.some(f => f.endsWith('.php')) || sourceCode['composer.json'],
    kotlin: () => files.some(f => f.endsWith('.kt')) || sourceCode['build.gradle.kts'],
    cpp: () => files.some(f => f.endsWith('.cpp') || f.endsWith('.cc')) || sourceCode['CMakeLists.txt'],
    qsharp: () => files.some(f => f.endsWith('.qs')),
    elixir: () => files.some(f => f.endsWith('.ex') || f.endsWith('.exs')) || sourceCode['mix.exs'],
    crud: () => files.some(f => f.includes('collection') && f.endsWith('.json')) || sourceCode['crud.config.json'],
    dotnet: () => {
      const hasCsproj = files.some(f => f.endsWith('.csproj'));
      const hasCsFiles = files.some(f => f.endsWith('.cs'));
      const hasRazor = files.some(f => f.endsWith('.cshtml') || f.endsWith('.razor'));
      return hasCsproj && hasCsFiles && !hasRazor; // Backend API, not Blazor frontend
    },
    node: () => {
      if (sourceCode['package.json']) {
        try {
          const packageJsonContent = sourceCode['package.json'];
          const pkg = typeof packageJsonContent === 'string' 
            ? JSON.parse(packageJsonContent) 
            : packageJsonContent;
          const deps = {...(pkg.dependencies || {}), ...(pkg.devDependencies || {})};
          // Check for backend frameworks
          return !!(deps.express || deps.fastify || deps.koa || deps.hapi || deps.nestjs || deps['@mia-platform/custom-plugin-lib']);
        } catch (e) {
          return false;
        }
      }
      return false;
    }
  };
  
  // Check backend frameworks first
  for (const [framework, detector] of Object.entries(backendIndicators)) {
    if (detector()) {
      const isCrud = framework === 'crud';
      return {isFrontend: false, isBackend: true, isCrud, detectedFramework: framework};
    }
  }
  
  // PRIORITY 3: Generic frontend file patterns (fallback if no specific framework detected)
  const hasFrontendFiles = 
    files.some(f => f.endsWith('.jsx') || f.endsWith('.tsx') || f.endsWith('.vue') || f.endsWith('.svelte')) ||
    sourceCode['index.html'] ||
    sourceCode['vite.config.js'] ||
    sourceCode['webpack.config.js'] ||
    sourceCode['craco.config.js'];
  
  if (hasFrontendFiles) {
    return {isFrontend: true, isBackend: false, isCrud: false, detectedFramework: 'frontend'};
  }
  
  // Unknown
  return {isFrontend: false, isBackend: false, isCrud: false, detectedFramework: null};
};

/**
 * Detect frontend framework from source code
 * @param {Object} sourceCode - Source code files object
 * @returns {string} - Framework type ('react', 'nextjs', 'angular', 'vue', 'jquery', 'docusaurus', 'reactnative', 'aspnet-frontend')
 */
export const detectFrontendFramework = (sourceCode) => {
  if (!sourceCode || Object.keys(sourceCode).length === 0) {
    return 'react'; // default
  }
  
  // Check frameworks in order of specificity
  if (detectDocusaurus(sourceCode)) return 'docusaurus';
  if (detectReactNative(sourceCode)) return 'reactnative';
  if (detectNextJs(sourceCode)) return 'nextjs';
  if (detectAngular(sourceCode)) return 'angular';
  if (detectVue(sourceCode)) return 'vue';
  if (detectJQuery(sourceCode)) return 'jquery';
  if (detectASPNetFrontend(sourceCode)) return 'aspnet-frontend';
  if (detectViteReact(sourceCode)) return 'react'; // Vite-based React
  
  // Default to React
  return 'react';
};

/**
 * Get complete container routing information for a service
 * This centralizes all detection logic to avoid duplication
 * @param {Object} service - The service object from Mia Platform
 * @param {Object} sourceCode - Source code files object
 * @param {Object} options - Optional context { activePreviewTab, previewSessionId }
 * @returns {Object} - Complete routing info with all necessary properties
 */
export const getContainerRoutingInfo = (service, sourceCode, options = {}) => {
  const { activePreviewTab, previewSessionId } = options;
  
  if (!service || !sourceCode) {
    return null;
  }
  
  const serviceDetection = detectServiceType(service, sourceCode);
  
  if (serviceDetection.isBackend) {
    // BACKEND SERVICE
    const framework = serviceDetection.isCrud ? 'crud' : detectBackendFramework(sourceCode);
    const config = FRAMEWORK_CONFIG[framework] || FRAMEWORK_CONFIG['node'];
    
    return {
      isBackend: true,
      isFrontend: false,
      isCrud: serviceDetection.isCrud,
      framework,
      containerPort: config.port,
      containerName: config.name,
      sessionId: activePreviewTab?.sessionId || previewSessionId,
      frameworkDisplay: config.display
    };
  } else {
    // FRONTEND SERVICE
    const framework = detectFrontendFramework(sourceCode);
    const config = FRAMEWORK_CONFIG[framework];
    
    return {
      isBackend: false,
      isFrontend: true,
      isCrud: false,
      framework,
      containerPort: activePreviewTab?.containerPort || config.port,
      containerName: config.name,
      sessionId: activePreviewTab?.sessionId || previewSessionId,
      frameworkDisplay: config.display
    };
  }
};

/**
 * Intelligently detect if a service is a frontend or backend based on multiple signals
 * @param {Object} service - The service object from Mia Platform
 * @param {Object} sourceCode - Optional source code files for more accurate detection
 * @returns {Object} - {isFrontend: boolean, isBackend: boolean, isCrud: boolean, confidence: string}
 */
export const detectServiceType = (service, sourceCode = null) => {
  if (!service) {
    return {isFrontend: false, isBackend: false, isCrud: false, confidence: 'none'};
  }
  
  // PRIORITY 1: If source code is provided, analyze it directly (most reliable)
  if (sourceCode && Object.keys(sourceCode).length > 0) {
    const fileDetection = detectServiceTypeFromFiles(sourceCode);
    if (fileDetection.isBackend || fileDetection.isFrontend) {
      console.log(`🎯 detectServiceType from FILES for "${service.name}":`, fileDetection);
      return {
        isFrontend: fileDetection.isFrontend,
        isBackend: fileDetection.isBackend,
        isCrud: fileDetection.isCrud,
        confidence: 'high'
      };
    }
  }
  
  // PRIORITY 2: Check explicit tags (high confidence)
  if (service.tags) {
    if (service.tags.includes('frontend') || service.tags.includes('fe')) {
      return {isFrontend: true, isBackend: false, isCrud: false, confidence: 'high'};
    }
    if (service.tags.includes('backend') || service.tags.includes('be')) {
      return {isFrontend: false, isBackend: true, isCrud: false, confidence: 'high'};
    }
    if (service.tags.includes('crud') || service.tags.includes('crud-service')) {
      return {isFrontend: false, isBackend: true, isCrud: true, confidence: 'high'};
    }
  }
  
  // Check if service has a repository URL (indicates source code is available)
  const hasSourceCode = !!(service.repoUrl || service.sshUrl || service.repositoryUrl);
  
  // PRIORITY 3: Check service name patterns (medium confidence)
  const name = (service.name || '').toLowerCase();
  const frontendPatterns = ['frontend', 'fe', 'react', 'vue', 'angular', 'svelte', 'next', 'web', 'ui', 'client', 'native', 'app', 'docusaurus', 'docs'];
  const backendPatterns = ['backend', 'be', 'api', 'server', 'service', 'node', 'go', 'python', 'java', 'dotnet', 'rust'];
  const crudPatterns = ['crud', 'crud-service', 'collection'];
  
  const hasFrontendName = frontendPatterns.some(pattern => name.includes(pattern));
  const hasBackendName = backendPatterns.some(pattern => name.includes(pattern));
  const hasCrudName = crudPatterns.some(pattern => name.includes(pattern));
  
  // Debug logging
  console.log(`🔍 detectServiceType from METADATA for "${service.name}":`, {
    hasFrontendName,
    hasBackendName,
    hasCrudName,
    hasSourceCode
  });
  
  // Check docker image patterns (medium confidence)
  const dockerImage = (service.dockerImage || '').toLowerCase();
  const hasFrontendImage = dockerImage.includes('nginx') || dockerImage.includes('react') || 
                          dockerImage.includes('vue') || dockerImage.includes('angular') ||
                          dockerImage.includes('frontend') || dockerImage.includes('vite');
  const hasBackendImage = dockerImage.includes('node') || dockerImage.includes('express') || 
                          dockerImage.includes('fastify') || dockerImage.includes('go') ||
                          dockerImage.includes('python') || dockerImage.includes('java') ||
                          dockerImage.includes('dotnet') || dockerImage.includes('rust');
  
  // Combine signals - prioritize BACKEND detection (backend patterns are more specific)
  if (hasCrudName) {
    return {isFrontend: false, isBackend: true, isCrud: true, confidence: 'medium'};
  }
  if (hasBackendName || hasBackendImage) {
    return {isFrontend: false, isBackend: true, isCrud: false, confidence: 'medium'};
  }
  if (hasFrontendName || hasFrontendImage) {
    return {isFrontend: true, isBackend: false, isCrud: false, confidence: 'medium'};
  }
  
  // Default: no clear signal
  return {isFrontend: false, isBackend: false, isCrud: false, confidence: 'none'};
};
